import { Box } from '@mui/material';
import { format as originalFormat } from 'date-fns';
import { Form, Formik, FormikErrors } from 'formik';
import React, { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { ResourceType } from '../../../../../../backend';
import { stopPropagation } from '../../../../../../utils/dom.utils';
import { RoundButton } from '../../../../../Button/RoundButton/RoundButton';
import { useRmsGanttValue } from '../../../RmsContext';
import { ganttDayFormat } from '../../gantt.constants';
import { useUpdateTurnaround } from '../useUpdateTurnaround';
import { BoardingGateInputs } from './BoardingGateInputs/BoardingGateInputs';
import { FlightsInformation } from './FlightsInformation';
import { ResourcesSelect } from './ResourcesSelect';
import { StyledButtonContainer, StyledContainer } from './TurnaroundForm.style';
import {
    getFlightsUpdateDto,
    getInitialValues,
    TurnaroundFormValues,
    validateActualTimes,
} from './TurnaroundForm.utils';

export const TurnaroundForm = memo(function TurnaroundForm({
    setAnchorEl,
}: {
    setAnchorEl: Dispatch<SetStateAction<HTMLButtonElement | null>>;
}) {
    const turnaround = useRmsGanttValue('selectedTurnarounds')?.[0];
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');

    const { updateAsyncFlights, isLoading } = useUpdateTurnaround({ optimisticUpdate: false });

    const handleSubmit = useCallback(
        async (values: TurnaroundFormValues) => {
            // to close modal after turnaround update
            await updateAsyncFlights({
                selectedDay: originalFormat(day, ganttDayFormat),
                selectedResourceType: resourceType,
                flightsUpdateDto: getFlightsUpdateDto(values),
            });
            setAnchorEl(null);
        },
        [day, resourceType, setAnchorEl, updateAsyncFlights],
    );

    const handleReset = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const handleValidate = useCallback((values: TurnaroundFormValues) => {
        const errors: FormikErrors<TurnaroundFormValues> = {};
        validateActualTimes(errors, values);

        return errors;
    }, []);

    return (
        <Formik
            initialValues={getInitialValues(turnaround)}
            onSubmit={handleSubmit}
            onReset={handleReset}
            validate={handleValidate}
        >
            {props => (
                <Form>
                    <StyledContainer onClick={stopPropagation}>
                        <FlightsInformation />
                        <ResourcesSelect />
                        {resourceType === ResourceType.BOARDING_GATE && <BoardingGateInputs />}

                        <StyledButtonContainer>
                            <Box mr={'5px'}>
                                <RoundButton
                                    color="primary"
                                    size="medium"
                                    variant="contained"
                                    type="submit"
                                    loading={isLoading}
                                    disabled={!props.dirty || !props.isValid}
                                    sx={{
                                        '&.Mui-disabled': {
                                            backgroundColor: '#006EBB',
                                        },
                                    }}
                                >
                                    <FormattedMessage id={'form.button.confirm'} />
                                </RoundButton>
                            </Box>
                            <Box marginLeft="5px">
                                <RoundButton color="secondary" size="medium" type="reset">
                                    <FormattedMessage id={'form.button.cancel'} />
                                </RoundButton>
                            </Box>
                        </StyledButtonContainer>
                    </StyledContainer>
                </Form>
            )}
        </Formik>
    );
});
